// src/components/forms/shared/SummaryRow.tsx
import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';

interface SummaryRowProps {
  label: string | React.ReactNode;
  value?: string | React.ReactNode;
  subRows?: Array<{
    label: string | React.ReactNode;
    value?: string | React.ReactNode;
    isTotal?: boolean;
    rowType?: 'default' | 'total';
    borderBottom?: string;
    borderTop?: string;
    backgroundColor?: string;
  }>;
  isTotal?: boolean;
  isBold?: boolean;
  backgroundColor?: string;
  borderLeft?: string;
  headerBackgroundColor?: string;
  headerBorderTop?: string;
  headerBorderBottom?: string;
  headerPadding?: string;
  totalRowBgColor?: string;
  totalRowTextColor?: string;
  subRowBorderBottom?: string;
  totalRowBorderBottom?: string;
  totalRowBorderTop?: string;
  subRowLabelColor?: string;
}

/**
 * A responsive summary row for booking details
 * On mobile: horizontal scroll if content overflows
 * On desktop: normal layout
 *
 * Main container customization:
 * - backgroundColor?: string - Override background color for main container
 * - borderLeft?: string - Override left border for main container
 * - headerBackgroundColor?: string - Override background color for header row only (with pet name/details)
 *
 * Optional customization:
 * - totalRowBgColor: Override background color for total rows
 * - totalRowTextColor: Override text color for total rows
 * - subRowBorderBottom: Override border for regular subrows
 * - totalRowBorderBottom: Override border-bottom for total rows only
 * - totalRowBorderTop: Override border-top for total rows only
 *
 * SubRow configuration:
 * - rowType?: 'default' | 'total' - Explicitly mark total rows (backward compatible with empty label)
 * - borderBottom?: string - Individual row border-bottom override (takes precedence over component-level config)
 * - borderTop?: string - Individual row border-top override (takes precedence over component-level config)
 *   Use 'none' to hide border for a specific row
 * - Empty label still triggers total styling for backward compatibility
 */
const SummaryRow: React.FC<SummaryRowProps> = ({
  label,
  value,
  subRows = [],
  isTotal = false,
  isBold = false,
  backgroundColor: bgColorProp,
  borderLeft: borderLeftProp,
  headerBackgroundColor,
  headerBorderTop,
  headerBorderBottom,
  headerPadding,
  totalRowBgColor,
  totalRowTextColor,
  subRowBorderBottom,
  totalRowBorderBottom,
  totalRowBorderTop,
  subRowLabelColor,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Always render with container styling - subRows will be empty if not provided
  return (
    <Box
      sx={{
        borderLeft: borderLeftProp || `3px solid ${theme.palette.primary.main}`,
        paddingLeft: '1rem',
        backgroundColor:
          bgColorProp ||
          (theme.palette.mode === 'dark'
            ? 'rgba(33, 150, 243, 0.05)'
            : 'rgba(33, 150, 243, 0.02)'),
        borderRadius: '4px',
      }}
    >
      {/* Header */}
      {(label || value) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: value ? 'space-between' : 'flex-start',
            alignItems: 'center',
            // marginBottom: '0.5rem',
            width: '100%',
            backgroundColor: headerBackgroundColor,
            borderTop: headerBorderTop,
            borderBottom: headerBorderBottom,
            padding: headerPadding || 0,
            overflowX: isMobile ? 'auto' : 'visible',
            overflowY: 'hidden',
            whiteSpace: 'nowrap',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            minWidth: 0,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {typeof label === 'string' ? (
            <Typography
              sx={{
                fontSize: '1.8rem',
                [theme.breakpoints.up('sm')]: {
                  fontSize: '2rem',
                },
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              {label}
            </Typography>
          ) : (
            <Box
              sx={{
                fontSize: '1.8rem',
                [theme.breakpoints.up('sm')]: {
                  fontSize: '2rem',
                },
                color: 'text.primary',
              }}
            >
              {label}
            </Box>
          )}
          {value &&
            (typeof value === 'string' ? (
              <Typography
                sx={{
                  fontSize: '1.8rem',
                  [theme.breakpoints.up('sm')]: {
                    fontSize: '2rem',
                  },
                  fontWeight: 700,
                  color: 'primary.main',
                  marginLeft: '1rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {value}
              </Typography>
            ) : (
              <Box
                sx={{
                  fontSize: '1.8rem',
                  [theme.breakpoints.up('sm')]: {
                    fontSize: '2rem',
                  },
                  fontWeight: 700,
                  color: 'primary.main',
                  marginLeft: '1rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {value}
              </Box>
            ))}
        </Box>
      )}

      {/* Subrows */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {subRows.map((row, idx) => {
          // Check if next row is a total row
          const nextRow = subRows[idx + 1];
          const isLastBeforeTotal =
            nextRow &&
            (nextRow.rowType === 'total' || nextRow.isTotal || !nextRow.label);

          return (
            <Box
              key={idx}
              sx={{
                borderBottom: isLastBeforeTotal
                  ? 'none'
                  : row.borderBottom !== undefined
                    ? row.borderBottom
                    : row.rowType === 'total' || row.isTotal || !row.label
                      ? totalRowBorderBottom ||
                        `2px solid ${theme.palette.primary.main}`
                      : subRowBorderBottom ||
                        `1px solid ${theme.palette.divider}`,
                borderTop:
                  row.borderTop !== undefined
                    ? row.borderTop
                    : row.rowType === 'total' || row.isTotal || !row.label
                      ? totalRowBorderTop ||
                        `2px solid ${theme.palette.primary.main}`
                      : 'none',
                paddingBottom: '0.4rem',
                paddingTop: !row.label ? '0.8rem' : '0.4rem',
                marginBottom: isLastBeforeTotal ? 0 : 0,
                backgroundColor:
                  row.backgroundColor ||
                  (row.rowType === 'total' || row.isTotal || !row.label
                    ? totalRowBgColor ||
                      (theme.palette.mode === 'dark'
                        ? 'rgba(33, 150, 243, 0.05)'
                        : 'rgba(33, 150, 243, 0.1)')
                    : 'transparent'),
              }}
            >
              {/* Scroll wrapper - individual row scrolls if content overflows */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: !row.label ? 'flex-end' : 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  overflowX: isMobile ? 'auto' : 'visible',
                  overflowY: 'hidden',
                  whiteSpace: 'nowrap',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  minWidth: 0,
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                {row.label &&
                  (typeof row.label === 'string' ? (
                    <Typography
                      sx={{
                        fontSize: '1.8rem',
                        color: row.value
                          ? subRowLabelColor || 'text.secondary'
                          : 'text.primary',
                        fontWeight: row.value ? 400 : 500,
                        flex: '0 0 auto',
                        marginRight: '1rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.label}
                    </Typography>
                  ) : (
                    <Box sx={{ flex: '0 0 auto', marginRight: '1rem' }}>
                      {row.label}
                    </Box>
                  ))}
                {row.value &&
                  (typeof row.value === 'string' ? (
                    <Typography
                      sx={{
                        fontSize: '1.8rem',
                        color:
                          row.rowType === 'total' || row.isTotal || !row.label
                            ? totalRowTextColor || 'primary.main'
                            : 'text.primary',
                        fontWeight: !row.label ? 700 : 500,
                      }}
                    >
                      {row.value}
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        fontSize: '1.5rem',
                        color:
                          row.rowType === 'total' || row.isTotal || !row.label
                            ? totalRowTextColor || 'primary.main'
                            : 'text.primary',
                        fontWeight: !row.label ? 700 : 500,
                      }}
                    >
                      {row.value}
                    </Box>
                  ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SummaryRow;
